import {
  ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import type { WebhookInstallStatus } from '@cotera/client/app/v1/webhook-providers/types';
import type { WebhookTriggerInstallState } from './types';
import type {
  WebhookTriggerPreviewState,
  WorkflowWebhookTriggerNodeModel,
} from './webhook-trigger-node.types';
import { WorkflowEntityNodeModel } from './workflow-entity-node-model';

const DEFAULT_PREVIEW_STATE: WebhookTriggerPreviewState = {
  installStatus: 'draft',
  installError: null,
  hasSecret: false,
  enabled: true,
};

function toInstallState(
  preview: WebhookTriggerPreviewState
): WebhookTriggerInstallState {
  return {
    installStatus: preview.installStatus,
    installError: preview.installError,
    loaded: true,
  };
}

/**
 * Draft / unsaved webhook trigger on the workflow canvas. State is driven by
 * the local workflow graph snapshot (updated on every draft graph sync).
 */
export class WorkflowWebhookTriggerPreviewNodeModel
  extends WorkflowEntityNodeModel
  implements WorkflowWebhookTriggerNodeModel
{
  readonly mode = 'preview' as const;
  readonly triggerId: string;
  readonly installStateWatchable: ReadonlyWatchable<WebhookTriggerInstallState>;
  readonly installStatusWatchable: ReadonlyWatchable<WebhookInstallStatus>;
  readonly installErrorWatchable: ReadonlyWatchable<{
    code?: string;
    message?: string;
  } | null>;
  readonly hasSecretWatchable: ReadonlyWatchable<boolean>;

  private readonly _previewWatchable: Watchable<WebhookTriggerPreviewState>;

  constructor(triggerId: string, initial?: WebhookTriggerPreviewState) {
    super(triggerId);
    this.triggerId = triggerId;
    this._previewWatchable = Watchable.fromValue(
      initial ?? DEFAULT_PREVIEW_STATE
    );
    this.installStateWatchable = Watchable.from((get) =>
      toInstallState(get(this._previewWatchable))
    );
    this.installStatusWatchable = Watchable.from(
      (get) => get(this._previewWatchable).installStatus
    );
    this.installErrorWatchable = Watchable.from(
      (get) => get(this._previewWatchable).installError
    );
    this.hasSecretWatchable = Watchable.from(
      (get) => get(this._previewWatchable).hasSecret
    );
  }

  syncFromGraph(preview: WebhookTriggerPreviewState): void {
    this._previewWatchable.set(preview);
  }

  reconcileFromGraph(_params: {
    installStatus?: WebhookInstallStatus;
    hasSecret?: boolean;
    enabled?: boolean;
  }): void {}

  dispose(): void {}
}
